import { useState } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  openDoor,
  ApiError,
  type DoorWhich,
} from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';

interface PendingOpen {
  which: DoorWhich;
  label: string;
}

export function DoorsPage() {
  const { push } = useToast();
  const [pending, setPending] = useState<PendingOpen | null>(null);

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      await openDoor(pending.which);
      push('success', `${pending.label} door opened.`);
      setPending(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Open failed';
      push('error', msg);
      setPending(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 480 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Doors</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          Each click sends an &quot;open&quot; command to the door controller. Use sparingly.
        </p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <DoorButton
          label="Outside"
          onClick={() => setPending({ which: 'outside', label: 'Outside' })}
        />
        <DoorButton
          label="Inside"
          onClick={() => setPending({ which: 'inside', label: 'Inside' })}
        />
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={`Open ${pending?.label ?? ''} door?`}
        message={`This sends an unlock command to the ${pending?.which ?? ''} door. Anyone within proximity will be able to enter.`}
        confirmLabel="Open"
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function DoorButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      style={{
        padding: '20px 24px',
        fontSize: 18,
        letterSpacing: '1.28px',
        minHeight: 72,
      }}
    >
      Open {label}
    </Button>
  );
}
